import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, BookOpenIcon, HeadphonesIcon, PlayIcon } from 'lucide-react';
import { AudioPlayer } from './AudioPlayer';
export function PackSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="the-pack" ref={ref} className="pt-16 pb-4 md:pt-24 md:pb-8" data-id="element-421">
      <div className="max-w-5xl mx-auto px-6" data-id="element-422">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-6" data-id="element-423">
          <span className="inline-block px-4 py-1.5 text-xs font-semibold tracking-wider uppercase border rounded-full bg-amber-500/20 text-amber-400 border-amber-500/30 mb-6" data-id="element-424">
            A Complete Pack
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-425">
            Ebook + Audiobook Included
          </h2>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="text-neutral-400 text-lg text-center max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-426">
          You don't have to choose. When you get the book you receive both
          formats — read it at your own pace and listen while you meditate.
        </motion.p>

        {/* Two columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-427">
          {/* Ebook */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="p-6 md:p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800/50" data-id="element-428">
            <div className="flex items-center gap-3 mb-6" data-id="element-429">
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center" data-id="element-430">
                <BookOpenIcon className="w-5 h-5 text-teal-400" data-id="element-431" />
              </div>
              <div data-id="element-432">
                <p className="text-teal-400 text-xs font-bold tracking-wider uppercase" data-id="element-433">
                  Included
                </p>
                <h3 className="text-white font-semibold text-lg" data-id="element-434">Ebook</h3>
              </div>
            </div>
            <p className="text-neutral-500 text-sm mb-5" data-id="element-435">Digital Format</p>
            <div className="space-y-3" data-id="element-436">
              <div className="flex items-center gap-3" data-id="element-437">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-438" />
                <span className="text-neutral-300 text-sm" data-id="element-439">
                  13 complete chapters
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-440">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-441" />
                <span className="text-neutral-300 text-sm" data-id="element-442">
                  Read on any device
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-443">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-444" />
                <span className="text-neutral-300 text-sm" data-id="element-445">
                  Instant download
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-446">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-447" />
                <span className="text-neutral-300 text-sm" data-id="element-448">
                  PDF + EPUB format
                </span>
              </div>
            </div>
          </motion.div>

          {/* Audiobook */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="p-6 md:p-8 rounded-2xl bg-neutral-900/50 border border-neutral-800/50" data-id="element-449">
            <div className="flex items-center gap-3 mb-6" data-id="element-450">
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center" data-id="element-451">
                <HeadphonesIcon className="w-5 h-5 text-teal-400" data-id="element-452" />
              </div>
              <div data-id="element-453">
                <p className="text-teal-400 text-xs font-bold tracking-wider uppercase" data-id="element-454">
                  Included
                </p>
                <h3 className="text-white font-semibold text-lg" data-id="element-455">Audiobook</h3>
              </div>
            </div>
            <p className="text-neutral-500 text-sm mb-5" data-id="element-456">Immersive Narration</p>
            <div className="space-y-3 mb-6" data-id="element-457">
              <div className="flex items-center gap-3" data-id="element-458">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-459" />
                <span className="text-neutral-300 text-sm" data-id="element-460">
                  Full professional narration
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-461">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-462" />
                <span className="text-neutral-300 text-sm" data-id="element-463">
                  Perfect for meditation
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-464">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-465" />
                <span className="text-neutral-300 text-sm" data-id="element-466">Listen offline</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-467">
                <CheckIcon className="w-4 h-4 text-teal-400 flex-shrink-0" data-id="element-468" />
                <span className="text-neutral-300 text-sm" data-id="element-469">
                  High quality MP3
                </span>
              </div>
            </div>

            {/* Embedded Audio Player */}
            <div className="pt-4 border-t border-neutral-800/50 mt-6" data-id="element-470">
              <AudioPlayer data-id="element-471" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}